import { useContext } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import router from 'next/router'

import { AuthorizationContext } from 'src/shared/context'

import { apiGet } from '../config'
import { API_ROUTES } from '../routes'
import { useMutationHook } from '../mutations/mutationHook'

interface ISignAgreementParams {
  id: string
}

async function signAgreement(path: string, params: ISignAgreementParams, token: string | null) {
  const headers = { Authorization: `Bearer ${token}` }

  try {
    const { data } = await apiGet<ISignAgreementParams, void>({ path, params, headers })

    return data
  } catch (error: any) {
    if (error.response?.status === 401) {
      throw new Error('Unauthorized')
    }
    throw error
  }
}

export const useSignAgreement = (
  id: string,
  options?: { onSuccess?: (data: void) => void; onError?: (error: Error) => void },
) => {
  const queryClient = useQueryClient()
  const { token, setToken } = useContext(AuthorizationContext)
  const path = API_ROUTES.AGREEMENT_SIGN

  return useMutationHook<ISignAgreementParams, void, Error>({
    path,
    mutationKey: ['agreements'],
    mutationFn: (url: string, params: ISignAgreementParams) => signAgreement(url, params, token),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [API_ROUTES.SIGNED_AGREEMENTS],
      })
      queryClient.invalidateQueries({
        queryKey: [API_ROUTES.UNSIGNED_AGREEMENTS],
      })
      queryClient.invalidateQueries({
        queryKey: [API_ROUTES.AGREEMENT_GET, id],
      })

      options?.onSuccess?.()
    },
    onError: (error: Error) => {
      if (error.message === 'Unauthorized') {
        setToken('')
        router.push('/')
      }
      options?.onError?.(error)
    },
  })
}
